import { useState } from "react";

import type { DepartmentSchema, TeamSchema } from "@nexttrack/api-schema";
import {
    useGetDepartmentsQuery,
    useCreateDepartmentMutation,
    useUpdateDepartmentMutation,
    useDeleteDepartmentMutation,
} from "@/store/services/departments/departments.service";
import {
    useGetTeamsQuery,
    useCreateTeamMutation,
    useUpdateTeamMutation,
    useDeleteTeamMutation,
} from "@/store/services/teams/teams.service";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type EditDeptTarget = DepartmentSchema | null;
type EditTeamTarget = TeamSchema | null;

export const DepartmentsPage = () => {
    const { data: departments, isLoading: isDepartmentsLoading } = useGetDepartmentsQuery();
    const [createDepartment, { isLoading: isCreating }] = useCreateDepartmentMutation();
    const [updateDepartment, { isLoading: isUpdating }] = useUpdateDepartmentMutation();
    const [deleteDepartment] = useDeleteDepartmentMutation();

    const { data: teams, isLoading: isTeamsLoading } = useGetTeamsQuery();
    const [createTeam, { isLoading: isCreatingTeam }] = useCreateTeamMutation();
    const [updateTeam, { isLoading: isUpdatingTeam }] = useUpdateTeamMutation();
    const [deleteTeam] = useDeleteTeamMutation();

    // New department dialog
    const [newDeptOpen, setNewDeptOpen] = useState(false);
    const [newDeptName, setNewDeptName] = useState("");

    // Edit department dialog
    const [editDeptTarget, setEditDeptTarget] = useState<EditDeptTarget>(null);
    const [editDeptName, setEditDeptName] = useState("");
    const [editDeptArchived, setEditDeptArchived] = useState(false);

    // New team dialog
    const [newTeamOpen, setNewTeamOpen] = useState(false);
    const [newTeamName, setNewTeamName] = useState("");
    const [newTeamDeptId, setNewTeamDeptId] = useState("");

    // Edit team dialog
    const [editTeamTarget, setEditTeamTarget] = useState<EditTeamTarget>(null);
    const [editTeamName, setEditTeamName] = useState("");
    const [editTeamDeptId, setEditTeamDeptId] = useState("");

    const activeDepartments = departments?.filter((d) => !d.archived) ?? [];

    const handleCreateDept = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newDeptName.trim()) return;
        await createDepartment({ name: newDeptName.trim() });
        setNewDeptName("");
        setNewDeptOpen(false);
    };

    const openEditDept = (dept: DepartmentSchema) => {
        setEditDeptTarget(dept);
        setEditDeptName(dept.name);
        setEditDeptArchived(dept.archived);
    };

    const handleUpdateDept = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editDeptTarget) return;
        await updateDepartment({
            id: editDeptTarget.id,
            data: { name: editDeptName.trim(), archived: editDeptArchived },
        });
        setEditDeptTarget(null);
    };

    const handleDeleteDept = async (id: string) => {
        if (!confirm("Delete this department?")) return;
        await deleteDepartment(id);
    };

    const handleCreateTeam = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newTeamName.trim() || !newTeamDeptId) return;
        await createTeam({ name: newTeamName.trim(), departmentId: newTeamDeptId });
        setNewTeamName("");
        setNewTeamDeptId("");
        setNewTeamOpen(false);
    };

    const openEditTeam = (team: TeamSchema) => {
        setEditTeamTarget(team);
        setEditTeamName(team.name);
        setEditTeamDeptId(team.departmentId);
    };

    const handleUpdateTeam = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editTeamTarget) return;
        await updateTeam({ id: editTeamTarget.id, data: { name: editTeamName.trim(), departmentId: editTeamDeptId } });
        setEditTeamTarget(null);
    };

    const handleDeleteTeam = async (id: string) => {
        if (!confirm("Delete this team?")) return;
        await deleteTeam(id);
    };

    const getDepartmentName = (departmentId: string) => departments?.find((d) => d.id === departmentId)?.name ?? "—";

    return (
        <div className="space-y-10">
            {/* Departments section */}
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-bold">Departments</h1>
                    <Button onClick={() => setNewDeptOpen(true)}>New Department</Button>
                </div>

                {isDepartmentsLoading ? (
                    <div className="space-y-2">
                        {[...Array(4)].map((_, i) => (
                            <Skeleton key={i} className="h-10 w-full" />
                        ))}
                    </div>
                ) : (
                    <div className="border rounded-lg overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {departments?.map((dept) => (
                                    <TableRow key={dept.id}>
                                        <TableCell className="font-medium">{dept.name}</TableCell>
                                        <TableCell>
                                            {dept.archived && <Badge variant="secondary">Archived</Badge>}
                                        </TableCell>
                                        <TableCell className="text-right space-x-2">
                                            <Button variant="outline" size="sm" onClick={() => openEditDept(dept)}>
                                                Edit
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={() => handleDeleteDept(dept.id)}
                                            >
                                                Delete
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {(!departments || departments.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={3} className="text-center text-slate-400 py-8">
                                            No departments yet. Create one to get started.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>

            {/* Teams section */}
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold">Teams</h2>
                    <Button onClick={() => setNewTeamOpen(true)}>New Team</Button>
                </div>

                {isTeamsLoading ? (
                    <div className="space-y-2">
                        {[...Array(4)].map((_, i) => (
                            <Skeleton key={i} className="h-10 w-full" />
                        ))}
                    </div>
                ) : (
                    <div className="border rounded-lg overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Department</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {teams?.map((team) => (
                                    <TableRow key={team.id}>
                                        <TableCell className="font-medium">{team.name}</TableCell>
                                        <TableCell>{getDepartmentName(team.departmentId)}</TableCell>
                                        <TableCell className="text-right space-x-2">
                                            <Button variant="outline" size="sm" onClick={() => openEditTeam(team)}>
                                                Edit
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={() => handleDeleteTeam(team.id)}
                                            >
                                                Delete
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {(!teams || teams.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={3} className="text-center text-slate-400 py-8">
                                            No teams yet. Create one to get started.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>

            {/* New Department Dialog */}
            <Dialog open={newDeptOpen} onOpenChange={setNewDeptOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>New Department</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateDept} className="space-y-4">
                        <div>
                            <label className="text-sm font-medium mb-1 block">Name</label>
                            <Input
                                value={newDeptName}
                                onChange={(e) => setNewDeptName(e.target.value)}
                                placeholder="e.g. Engineering"
                                autoFocus
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setNewDeptOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isCreating || !newDeptName.trim()}>
                                {isCreating ? "Creating..." : "Create"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit Department Dialog */}
            <Dialog open={!!editDeptTarget} onOpenChange={(open) => !open && setEditDeptTarget(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Department</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleUpdateDept} className="space-y-4">
                        <div>
                            <label className="text-sm font-medium mb-1 block">Name</label>
                            <Input value={editDeptName} onChange={(e) => setEditDeptName(e.target.value)} autoFocus />
                        </div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox
                                checked={editDeptArchived}
                                onCheckedChange={(v) => setEditDeptArchived(Boolean(v))}
                            />
                            Archived
                        </label>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setEditDeptTarget(null)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isUpdating || !editDeptName.trim()}>
                                {isUpdating ? "Saving..." : "Save"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* New Team Dialog */}
            <Dialog open={newTeamOpen} onOpenChange={setNewTeamOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>New Team</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateTeam} className="space-y-4">
                        <div>
                            <label className="text-sm font-medium mb-1 block">Name</label>
                            <Input
                                value={newTeamName}
                                onChange={(e) => setNewTeamName(e.target.value)}
                                placeholder="e.g. Frontend"
                                autoFocus
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-1 block">Department</label>
                            <Select value={newTeamDeptId} onValueChange={setNewTeamDeptId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a department" />
                                </SelectTrigger>
                                <SelectContent>
                                    {activeDepartments.map((dept) => (
                                        <SelectItem key={dept.id} value={dept.id}>
                                            {dept.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setNewTeamOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isCreatingTeam || !newTeamName.trim() || !newTeamDeptId}>
                                {isCreatingTeam ? "Creating..." : "Create"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit Team Dialog */}
            <Dialog open={!!editTeamTarget} onOpenChange={(open) => !open && setEditTeamTarget(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Team</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleUpdateTeam} className="space-y-4">
                        <div>
                            <label className="text-sm font-medium mb-1 block">Name</label>
                            <Input value={editTeamName} onChange={(e) => setEditTeamName(e.target.value)} autoFocus />
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-1 block">Department</label>
                            <Select value={editTeamDeptId} onValueChange={setEditTeamDeptId}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a department" />
                                </SelectTrigger>
                                <SelectContent>
                                    {activeDepartments.map((dept) => (
                                        <SelectItem key={dept.id} value={dept.id}>
                                            {dept.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setEditTeamTarget(null)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isUpdatingTeam || !editTeamName.trim() || !editTeamDeptId}>
                                {isUpdatingTeam ? "Saving..." : "Save"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};
